import React from 'react'

import {omit} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import invariant from 'invariant'
import * as Yup from 'yup'

import config from '../../config'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {setProvider} from '../../redux/features/core'
import {openSimpleContentModal} from '../../redux/features/modal'

import {captureException} from '../../../../commons/src/services/logging'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import * as providerApi from '../../services/api/provider'
import {toTouchedFormData} from '../utils/form'

import checkedIcon from '../../../../commons/src/assets/icons/checked.svg'
import uploadBoxIcon from '../../../../commons/src/assets/icons/upload-box.svg'
import partnerLogoExamplesImage from '../../assets/images/partner-logo-examples.png'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../../commons/src/components/button'
import FieldNote from '../../../../commons/src/components/field-note'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import Image from '../../../../commons/src/components/image'
import Link from '../../../../commons/src/components/link'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import Paragraph from '../../../../commons/src/components/paragraph'
import Section from '../../../../commons/src/components/section'
import SplitLayout from '../../../../commons/src/components/split-layout'
import Stack from '../../../../commons/src/components/stack'
import TextField from '../../../../commons/src/components/text-field'

import SettingsProfileCertificationsPartial from './settings-profile-certifications-partial'
import SettingsProfileImagesPartial from './settings-profile-images-partial'
import SettingsProfileServicesPartial from './settings-profile-services-partial'
import SettingsProfileSpecializationsPartial from './settings-profile-specializations-partial'

const initialProviderValues = {
  description: '',
  formikGeneral: '',
}

const SettingsProfilePartial = () => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const [isProviderSubmitted, setIsProviderSubmitted] = React.useState(false)
  const [isProviderLogoSubmitted, setIsProviderLogoSubmitted] = React.useState(false)

  const logoInputRef = React.useRef<HTMLInputElement>(null)

  const providerLogoFormik = useFormik<{
    providerLogo: {file: File; src: string} | undefined
    formikGeneral: string
  }>({
    enableReinitialize: true,
    initialValues: {
      providerLogo: undefined,
      formikGeneral: '',
    },
    validationSchema: Yup.object({providerLogo: Yup.mixed().required('Logo is required')}),
    onSubmit: async (values, actions) => {
      if (!values.providerLogo) return

      try {
        await providerApi.uploadLogo({file: values.providerLogo.file}, provider.id)
        const nextProvider = await providerApi.getById(provider.id)
        dispatch(setProvider({provider: nextProvider}))

        setIsProviderLogoSubmitted(true)

        if (values.providerLogo?.src) {
          URL.revokeObjectURL(values.providerLogo.src) // free memory
        }
        actions.resetForm()
      } catch (error) {
        captureException(error)
        actions.setFieldError('formikGeneral', `Update failed with: ${(error as Error).message}`)
      }
    },
  })

  const providerFormik = useFormik<{description: string; formikGeneral: string}>({
    enableReinitialize: true,
    initialValues: {
      ...initialProviderValues,
      description: provider.description || '',
    },
    validationSchema: Yup.object({
      description: Yup.string()
        .label('Description')
        .min(
          config.providerDescription.minLength,
          `Description must be more than ${config.providerDescription.minLength} characters.`
        )
        .max(
          config.providerDescription.maxLength,
          `Description must be less than ${config.providerDescription.maxLength} characters.`
        )
        .required('Description is required'),
    }),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(values, initialProviderValues)
      const providerRequest = omit(touchedValues, ['formikGeneral'])

      try {
        const nextProvider = await providerApi.patch(providerRequest, provider.id)

        setIsProviderSubmitted(true)
        dispatch(setProvider({provider: nextProvider}))
      } catch (error) {
        captureException(error)
        actions.setFieldError('formikGeneral', `Update failed with: ${(error as Error).message}`)
      }
    },
  })

  const onLogoInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0]
      providerLogoFormik.setFieldError('formikGeneral', '')
      providerLogoFormik.setFieldValue('providerLogo', {file, src: URL.createObjectURL(file)})
      setIsProviderLogoSubmitted(false)
    }
    // Clear file input
    event.target.value = ''
  }

  const renderLogoSection = () => (
    <Section>
      <Stack>
        <SplitLayout
          left={<Headline label="Logo" className="u-no-margin-bottom" />}
          right={
            <>
              <Button
                hierarchy="secondary"
                icon={<Icon source={uploadBoxIcon} />}
                onClick={() => logoInputRef.current?.click()}
                disabled={providerLogoFormik.isSubmitting}
              >
                Change
              </Button>
              <input
                type="file"
                id="logo-input"
                accept="image/png"
                ref={logoInputRef}
                style={{display: 'none'}}
                onChange={onLogoInputChange}
                disabled={providerLogoFormik.isSubmitting}
              />
            </>
          }
        />
        <Paragraph className="u-no-margin-bottom">
          We will show your logo on Craftcloud as it is displayed here: on a white background with a
          maximum height of 100px while maintaining the aspect ratio.
          <br />
          Please make sure that your logo is in <strong>PNG</strong> format, with a{' '}
          <strong>height of 100px</strong>, has a <strong>transparent background</strong> and has{' '}
          <strong>no large white spaces</strong> (see{' '}
          <Link
            onClick={() => {
              dispatch(
                openSimpleContentModal({
                  title: "Logo - Dos and Don'ts",
                  content: <Image src={partnerLogoExamplesImage} width="600" alt="" />,
                })
              )
            }}
          >
            Dos and Don&apos;ts
          </Link>{' '}
          here).
        </Paragraph>
        <form onSubmit={providerLogoFormik.handleSubmit}>
          <Stack>
            {providerLogoFormik.values.providerLogo?.src || provider.image ? (
              <Image
                height="100"
                fit="contain"
                src={providerLogoFormik.values.providerLogo?.src || provider.image || ''}
                alt="provider logo"
              />
            ) : (
              <Paragraph>No company logo provided</Paragraph>
            )}
            {providerLogoFormik.errors.formikGeneral && (
              <Paragraph error>{providerLogoFormik.errors.formikGeneral}</Paragraph>
            )}
          </Stack>
        </form>
        <ActionBar>
          <Button
            disabled={
              providerLogoFormik.isSubmitting ||
              isProviderLogoSubmitted ||
              !providerLogoFormik.dirty
            }
            icon={
              isProviderLogoSubmitted ? (
                <Icon source={checkedIcon} />
              ) : providerLogoFormik.isSubmitting &&
                providerLogoFormik.submitCount > 0 &&
                isEmpty(providerLogoFormik.errors) ? (
                <LoadingIndicator invert />
              ) : undefined
            }
            onClick={preventDoubleClick(() => providerLogoFormik.handleSubmit())}
          >
            {isProviderLogoSubmitted ? 'Saved' : 'Save'}
          </Button>
          <Button
            hierarchy="secondary"
            disabled={
              providerLogoFormik.isSubmitting ||
              isProviderLogoSubmitted ||
              !providerLogoFormik.dirty
            }
            onClick={preventDoubleClick(() => {
              providerLogoFormik.resetForm()
            })}
          >
            Reset
          </Button>
        </ActionBar>
      </Stack>
    </Section>
  )

  const renderDescriptionSection = () => (
    <Section>
      <Stack>
        <Headline label="Description" className="u-no-margin-bottom" />
        <Paragraph className="u-no-margin-bottom">
          Provide a brief overview of your service to present yourself to Craftcloud customers.
        </Paragraph>
        <form onSubmit={providerFormik.handleSubmit}>
          <FieldNote
            appearance="error"
            note={providerFormik.submitCount > 0 && providerFormik.errors.description}
            className="u-margin-bottom"
          >
            <TextField
              name="description"
              placeholder="Description"
              rows={7}
              maxLength={config.providerDescription.maxLength}
              value={providerFormik.values.description}
              appearance={
                providerFormik.submitCount > 0 && !!providerFormik.errors.description
                  ? 'error'
                  : undefined
              }
              onChange={(value, name) => {
                providerFormik.setFieldValue(name as string, value)
                if (isProviderSubmitted) {
                  setIsProviderSubmitted(false)
                }
              }}
              onBlur={providerFormik.handleBlur}
            />
            {providerFormik.errors.formikGeneral && (
              <Paragraph error>{providerFormik.errors.formikGeneral}</Paragraph>
            )}
          </FieldNote>
        </form>
        <ActionBar>
          <Button
            disabled={providerFormik.isSubmitting || isProviderSubmitted || !providerFormik.dirty}
            icon={
              isProviderSubmitted ? (
                <Icon source={checkedIcon} />
              ) : providerFormik.isSubmitting &&
                providerFormik.submitCount > 0 &&
                isEmpty(providerFormik.errors) ? (
                <LoadingIndicator invert />
              ) : undefined
            }
            onClick={preventDoubleClick(() => providerFormik.handleSubmit())}
          >
            {isProviderSubmitted ? 'Saved' : 'Save'}
          </Button>
          <Button
            hierarchy="secondary"
            disabled={providerFormik.isSubmitting || isProviderSubmitted || !providerFormik.dirty}
            onClick={preventDoubleClick(() => {
              providerFormik.resetForm()
            })}
          >
            Reset
          </Button>
        </ActionBar>
      </Stack>
    </Section>
  )

  return (
    <>
      {renderLogoSection()}
      {renderDescriptionSection()}
      <SettingsProfileImagesPartial providerId={provider.id} />
      <SettingsProfileSpecializationsPartial providerId={provider.id} />
      <SettingsProfileServicesPartial providerId={provider.id} />
      <SettingsProfileCertificationsPartial providerId={provider.id} />
    </>
  )
}

export default SettingsProfilePartial
